import {SocialSignInProvider} from "../../client/models/authentication/SocialSignInProvider";
import {appRoutePaths} from "../../config/config";

const routeAfterSignInQueryParam = "route-to-page-after-sign-in";

/**
 * Password sign-in reads this query value and opens it after sign-in.
 * Only an in-app path is accepted. Anything else falls back to Boot Camp.
 */
function normalizeRouteAfterSignIn(value: string | null | undefined): string | undefined {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("://") || value.includes("\\")) {
    return undefined;
  }
  return value;
}

export interface SocialLinkHandoff {
  linkingToken: string;
  prefillEmail: string | null;
  /** False when a prefilled address still needs a code. Null when there is no prefill or the flag was absent. */
  isPrefillEmailVerified: boolean | null;
  provider: SocialSignInProvider;
  enteredEmail?: string;
  /** In-app path from route-to-page-after-sign-in. Absent when sign-in should open Boot Camp. */
  routeAfterSignIn?: string;
}

const STORAGE_KEYS = {
  linkingToken: "socialLinkingToken",
  prefillEmail: "socialPrefillEmail",
  prefillEmailVerified: "socialPrefillEmailVerified",
  provider: "socialProvider",
  enteredEmail: "socialEmail",
  routeAfterSignIn: "socialRouteAfterSignIn",
} as const;

function providerFromStorage(value: string | null): SocialSignInProvider | null {
  if (value === SocialSignInProvider.Google || value === SocialSignInProvider.Apple) {
    return value;
  }
  return null;
}

function verifiedFromStorage(value: string | null): boolean | null {
  if (value === "1") {
    return true;
  }
  if (value === "0") {
    return false;
  }
  return null;
}

export const socialLinkHandoff = {
  fromOtcSent(input: {
    linkingToken: string;
    email: string;
    provider: SocialSignInProvider;
    routeAfterSignIn?: string;
  }): SocialLinkHandoff {
    return {
      linkingToken: input.linkingToken,
      prefillEmail: input.email,
      isPrefillEmailVerified: false,
      provider: input.provider,
      enteredEmail: input.email,
      ...(input.routeAfterSignIn ? {routeAfterSignIn: input.routeAfterSignIn} : {}),
    };
  },

  persist(handoff: SocialLinkHandoff): void {
    try {
      sessionStorage.setItem(STORAGE_KEYS.linkingToken, handoff.linkingToken);
      sessionStorage.setItem(STORAGE_KEYS.prefillEmail, handoff.prefillEmail ?? "");
      if (handoff.isPrefillEmailVerified === true) {
        sessionStorage.setItem(STORAGE_KEYS.prefillEmailVerified, "1");
      } else if (handoff.isPrefillEmailVerified === false) {
        sessionStorage.setItem(STORAGE_KEYS.prefillEmailVerified, "0");
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.prefillEmailVerified);
      }
      sessionStorage.setItem(STORAGE_KEYS.provider, handoff.provider);
      if (handoff.enteredEmail) {
        sessionStorage.setItem(STORAGE_KEYS.enteredEmail, handoff.enteredEmail);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.enteredEmail);
      }
      const routeAfterSignIn = normalizeRouteAfterSignIn(handoff.routeAfterSignIn);
      if (routeAfterSignIn) {
        sessionStorage.setItem(STORAGE_KEYS.routeAfterSignIn, routeAfterSignIn);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.routeAfterSignIn);
      }
    } catch {
      // sessionStorage unavailable — router state is the primary transport
    }
  },

  restore(): (Omit<SocialLinkHandoff, "provider"> & {provider: SocialSignInProvider | null}) | null {
    try {
      const linkingToken = sessionStorage.getItem(STORAGE_KEYS.linkingToken);
      if (!linkingToken) {
        return null;
      }
      const routeAfterSignIn = normalizeRouteAfterSignIn(sessionStorage.getItem(STORAGE_KEYS.routeAfterSignIn));
      return {
        linkingToken,
        prefillEmail: sessionStorage.getItem(STORAGE_KEYS.prefillEmail) || null,
        isPrefillEmailVerified: verifiedFromStorage(sessionStorage.getItem(STORAGE_KEYS.prefillEmailVerified)),
        provider: providerFromStorage(sessionStorage.getItem(STORAGE_KEYS.provider)),
        enteredEmail: sessionStorage.getItem(STORAGE_KEYS.enteredEmail) ?? undefined,
        ...(routeAfterSignIn ? {routeAfterSignIn} : {}),
      };
    } catch {
      return null;
    }
  },

  routeAfterSignInFromSearch(searchParams: URLSearchParams): string | undefined {
    return normalizeRouteAfterSignIn(searchParams.get(routeAfterSignInQueryParam));
  },

  pageAfterSignIn(routeAfterSignIn: string | null | undefined): string {
    return normalizeRouteAfterSignIn(routeAfterSignIn) ?? appRoutePaths.firstPageAfterSignIn;
  },

  clear(): void {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // ignore
    }
  },
};
